/**
 * Alpine.js wishlist store for Shopify themes.
 *
 * Persists product handles to localStorage under the key `dh-wishlist`.
 * Must be loaded before Alpine initializes (or alongside it) so the store
 * is available when Alpine boots.
 *
 * Usage in markup:
 *   $store.wishlist.toggle(handle)
 *   $store.wishlist.has(handle)
 *   $store.wishlist.count
 */

const STORAGE_KEY = 'dh-wishlist';

/**
 * Loads the wishlist from localStorage.
 *
 * @returns {string[]} Array of product handle strings.
 */
function _loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Persists the given items array to localStorage.
 *
 * @param {string[]} items - Array of product handle strings.
 * @returns {void}
 */
function _saveToStorage(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

document.addEventListener('alpine:init', () => {
  Alpine.store('wishlist', {
    /** @type {string[]} */
    items: _loadFromStorage(),

    /**
     * Adds a product handle to the wishlist if not already present.
     *
     * @param {string} handle - Shopify product handle.
     * @returns {void}
     */
    add(handle) {
      if (this.has(handle)) return;
      this.items = [...this.items, handle];
      _saveToStorage(this.items);
    },

    /**
     * Removes a product handle from the wishlist.
     *
     * @param {string} handle - Shopify product handle.
     * @returns {void}
     */
    remove(handle) {
      this.items = this.items.filter((h) => h !== handle);
      _saveToStorage(this.items);
    },

    /**
     * Adds the handle if absent, removes it if present.
     *
     * @param {string} handle - Shopify product handle.
     * @returns {void}
     */
    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
      } else {
        this.add(handle);
      }
    },

    /**
     * Returns true if the handle is in the wishlist.
     *
     * @param {string} handle - Shopify product handle.
     * @returns {boolean}
     */
    has(handle) {
      return this.items.includes(handle);
    },

    /**
     * The number of items currently in the wishlist.
     *
     * @returns {number}
     */
    get count() {
      return this.items.length;
    },

    /**
     * Removes all handles from the wishlist.
     *
     * @returns {void}
     */
    clear() {
      this.items = [];
      _saveToStorage(this.items);
    },

    /**
     * Fetches product data for every saved handle via the Shopify
     * `/products/{handle}.js` endpoint. Uses Promise.allSettled so that
     * deleted or unavailable products do not cause the entire batch to fail.
     *
     * @returns {Promise<Object[]>} Resolves with an array of product objects
     *   for handles that were fetched successfully.
     */
    async fetchProducts() {
      const results = await Promise.allSettled(
        this.items.map((handle) =>
          fetch(`/products/${handle}.js`).then((res) => {
            if (!res.ok) throw new Error(`Failed to fetch product: ${handle}`);
            return res.json();
          })
        )
      );

      return results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
    },
  });
});
